import { useTranslation } from 'react-i18next';
import { dateColumn } from '~/modules/common/data-table/columns';
import type { ColumnOrColumnGroup } from '~/modules/common/data-table/types';
import { EntityAvatar } from '~/modules/common/entity-avatar';
import { useHandleInvitationMutation } from '~/modules/me/query';
import type { Invitation } from '~/modules/me/types';
import { Button } from '~/modules/ui/button';
import { ActorCell } from '~/modules/user/actor-cell';

export const useColumns = () => {
  const { t } = useTranslation();

  const actionButtons = [
    { label: t('c:accept'), variant: 'success', action: 'accept' },
    { label: t('c:reject'), variant: 'destructive', action: 'reject' },
  ] as const;

  const { mutate: answerInvitation } = useHandleInvitationMutation();

  const columns: ColumnOrColumnGroup<Invitation>[] = [
    {
      key: 'name',
      name: '',
      renderCell: ({ row }) => (
        <>
          <EntityAvatar type={row.entity.entityType} className="size-8" id={row.entity.id} name={row.entity.name} url={row.entity.thumbnailUrl} />
          <span className="ml-2 truncate font-medium">{row.entity.name || '-'}</span>
        </>
      ),
    },
    {
      key: 'entityType',
      name: t('c:type'),
      renderCell: ({ row }) => t(`c:${row.entity.entityType}`),
    },
    {
      key: 'role',
      name: t('c:role'),
      width: 100,
      placeholderValue: '-',
      renderCell: ({ row }) => (row.inactiveMembership.role ? t(`c:${row.inactiveMembership.role}`) : null),
    },
    dateColumn<Invitation>('createdAt', { name: t('c:invited_at'), sortable: false, get: (row) => row.inactiveMembership.createdAt }),
    {
      key: 'createdBy',
      name: t('c:invited_by'),
      minBreakpoint: 'md',
      minWidth: 160,
      placeholderValue: '-',
      renderCell: ({ row, tabIndex }) =>
        row.inactiveMembership.createdBy && <ActorCell compactable actor={row.inactiveMembership.createdBy} tabIndex={tabIndex} />,
    },
    {
      key: 'actions',
      name: '',
      width: 200,
      renderCell: ({ row }) => (
        <div className="flex w-full max-w-50 gap-2">
          {actionButtons.map(({ label, variant, action }) => (
            <Button
              className="w-full"
              key={action}
              size="xs"
              variant={variant}
              onClick={() => answerInvitation({ path: { id: row.inactiveMembership.id, acceptOrReject: action } })}
            >
              {label}
            </Button>
          ))}
        </div>
      ),
    },
  ];

  return columns;
};
